import { SortDirection, ListDirection } from '../hooks/use-additional-infos/types';

export function getEdgeDirections(
    // Accepts `undefined` to match the renderer's `sortDirection` type. This is
    // behavior-identical to the original inline `sortDirection === SortDirection.DESC`
    // check, which treated `undefined` as "not DESC" → ASC edge directions.
    sortDirection: SortDirection | undefined
): {
    bottomEdgeDirection: ListDirection;
    topEdgeDirection: ListDirection;
} {
    const isDescSort = sortDirection === SortDirection.DESC;
    return {
        bottomEdgeDirection: isDescSort ? ListDirection.HISTORIC : ListDirection.CATCHUP,
        topEdgeDirection: isDescSort ? ListDirection.CATCHUP : ListDirection.HISTORIC,
    };
}

export function getOldestRowScrollTarget({
    isAscending,
    chronologicalStartIndex,
    dataLength,
}: {
    isAscending: boolean;
    chronologicalStartIndex: number;
    dataLength: number;
}): { index: number; align: 'start' | 'end' } | undefined {
    if (dataLength === 0) return undefined;
    return isAscending
        ? { index: chronologicalStartIndex, align: 'start' }
        : { index: chronologicalStartIndex + dataLength - 1, align: 'end' };
}

/**
 * Whether any chronological data row is in the virtual window. Gates the
 * newer-edge fetch so it can't fire while only non-data rows show (a pinned
 * section taller than the viewport) — the prepend would land off-screen and
 * re-fire, running pagination away. Callers skip it at
 * `dataLength === 0` (bootstrap).
 */
export function hasVisibleChronologicalRow(
    virtualItems: ReadonlyArray<{ index: number }>,
    chronologicalStartIndex: number,
    dataLength: number
): boolean {
    return virtualItems.some((item) => {
        const dataIndex = item.index - chronologicalStartIndex;
        return dataIndex >= 0 && dataIndex < dataLength;
    });
}

/**
 * Top padding that anchors the virtualized content to the bottom of the
 * viewport in ASC mode. In ASC the input box is at the bottom and the list
 * reads bottom-up like a chat; when the combined content (narratives + divider +
 * pinned) is shorter than the viewport the virtualizer would lay it out from the
 * top, leaving a gap below. Padding the top with the leftover space pushes the
 * rows down so the newest content sits just above the input. DESC keeps top
 * alignment, so the spacer is always 0 there.
 */
export function getBottomAnchorSpacer({
    isAscending,
    viewportHeight,
    totalSize,
}: {
    isAscending: boolean;
    viewportHeight: number;
    totalSize: number;
}): number {
    if (!isAscending) return 0;
    return Math.max(0, viewportHeight - totalSize);
}

/**
 * Stable virtualizer key for a combined-list row. A pinned entry also lives in
 * the chronological list at its real position, so both copies share the same
 * numeric `id`; the pinned copy is namespaced (`pinned-<id>`) so its key can't
 * collide with its chronological twin. A duplicate key makes the virtualizer
 * render the row multiple times — visible after expanding the pinned section
 * (which pulls more pinned rows into the loaded window) and then scrolling.
 */
export function getVirtualRowKey(item: {
    id: number | string;
    type?: string;
    edge?: string;
    _rowType?: string;
}): number | string {
    if (item.type === 'PINNED_DIVIDER') return 'pinned-divider';
    if (item.type === 'EDGE_LOADER') return `${item.edge}-loader`;
    if (item._rowType === 'pinned') return `pinned-${item.id}`;
    return item.id;
}

export function getNewestInLoadedId(
    data: ReadonlyArray<{ id: number }>,
    isAscending: boolean
): number | undefined {
    if (data.length === 0) return undefined;
    return isAscending ? data[data.length - 1].id : data[0].id;
}
